import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Database } from "@/integrations/supabase/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AIProviderSettings } from "./AIProviderSettings";
// import { AIChatModeSettings } from "./AIChatModeSettings";
import { AIChatbotSettings as AIChatbotSettingsType } from "@/types/ai";
import { useToast } from "@/components/ui/use-toast";
import { useBuilder } from "@/contexts/BuilderContext";
// import { useAIChatModeConfig } from "@/hooks/useAIChatModeConfig";

type ChatbotData = Database['public']['Tables']['chatbots']['Row'];

interface GeneralChatbotSettings {
  messageDelay: number;
  showTypingIndicator: boolean;
  chatMode?: 'question_flow' | 'ai_only'; // New chat mode setting
  buttonOptionsDelay?: number; // Delay before showing button options (0 = immediate)
  noResponseDelay?: number; // Delay for no_response questions to let users read
  optionsHandlerDelay?: number; // Delay in options handler for event dispatching
  validation_settings?: {
    enabled: boolean;
    maxAttempts: number;
    strictValidation: boolean;
    followUpStyle?: string;
  };
  ai_settings?: AIChatbotSettingsType;
}


interface BuilderSettingsProps {
  chatbot: ChatbotData | null | undefined;
  onUpdateSettings: (updates: Partial<ChatbotData>) => void;
  reportUnsavedChanges: (isDirty: boolean) => void;
}

export const BuilderSettings = ({
  chatbot,
  onUpdateSettings,
  reportUnsavedChanges
}: BuilderSettingsProps) => {
  const { toast } = useToast();

  // Use unified builder context instead of local state
  const { getChatbot, getSettings, updateSettings, debouncedSave, state } = useBuilder();
  const unifiedChatbot = getChatbot();
  const unifiedSettings = getSettings();

  // Local state only for UI-specific values that don't need persistence
  const [active, setActive] = useState(chatbot?.active || false);
  const [isSavingAISettings, setIsSavingAISettings] = useState(false);

  // Use local state for input values to prevent overwriting by server updates
  const [messageDelay, setMessageDelay] = useState(unifiedSettings?.messageDelay ?? 1000);
  const [showTypingIndicator, setShowTypingIndicator] = useState(unifiedSettings?.showTypingIndicator ?? true);
  const [buttonOptionsDelay, setButtonOptionsDelay] = useState(unifiedSettings?.buttonOptionsDelay ?? 0);
  const [noResponseDelay, setNoResponseDelay] = useState(unifiedSettings?.noResponseDelay ?? 2000);
  const [optionsHandlerDelay, setOptionsHandlerDelay] = useState(unifiedSettings?.optionsHandlerDelay ?? 10);
  const [validationEnabled, setValidationEnabled] = useState(unifiedSettings?.validation_settings?.enabled ?? false);
  const [validationMaxAttempts, setValidationMaxAttempts] = useState(unifiedSettings?.validation_settings?.maxAttempts ?? 2);
  const [validationStrict, setValidationStrict] = useState(unifiedSettings?.validation_settings?.strictValidation ?? false);

  // Use refs to track previous values and prevent unnecessary updates
  const prevMessageDelayRef = useRef<number | undefined>(undefined);
  const prevShowTypingIndicatorRef = useRef<boolean | undefined>(undefined);
  const prevButtonOptionsDelayRef = useRef<number | undefined>(undefined);
  const prevNoResponseDelayRef = useRef<number | undefined>(undefined);
  const prevOptionsHandlerDelayRef = useRef<number | undefined>(undefined);
  const prevValidationEnabledRef = useRef<boolean | undefined>(undefined);
  const prevValidationMaxAttemptsRef = useRef<number | undefined>(undefined);
  const prevValidationStrictRef = useRef<boolean | undefined>(undefined);

  // Sync local state with context when context updates (but only if not dirty)
  useEffect(() => {
    if (!state.isDirty && !state.unsavedChanges) {
      // Only update if value actually changed
      if (unifiedSettings?.messageDelay !== undefined && unifiedSettings.messageDelay !== prevMessageDelayRef.current) {
        setMessageDelay(unifiedSettings.messageDelay);
        prevMessageDelayRef.current = unifiedSettings.messageDelay;
      }
      if (unifiedSettings?.showTypingIndicator !== undefined && unifiedSettings.showTypingIndicator !== prevShowTypingIndicatorRef.current) {
        setShowTypingIndicator(unifiedSettings.showTypingIndicator);
        prevShowTypingIndicatorRef.current = unifiedSettings.showTypingIndicator;
      }
      if (unifiedSettings?.buttonOptionsDelay !== undefined && unifiedSettings.buttonOptionsDelay !== prevButtonOptionsDelayRef.current) {
        setButtonOptionsDelay(unifiedSettings.buttonOptionsDelay);
        prevButtonOptionsDelayRef.current = unifiedSettings.buttonOptionsDelay;
      }
      if (unifiedSettings?.noResponseDelay !== undefined && unifiedSettings.noResponseDelay !== prevNoResponseDelayRef.current) {
        setNoResponseDelay(unifiedSettings.noResponseDelay);
        prevNoResponseDelayRef.current = unifiedSettings.noResponseDelay;
      }
      if (unifiedSettings?.optionsHandlerDelay !== undefined && unifiedSettings.optionsHandlerDelay !== prevOptionsHandlerDelayRef.current) {
        setOptionsHandlerDelay(unifiedSettings.optionsHandlerDelay);
        prevOptionsHandlerDelayRef.current = unifiedSettings.optionsHandlerDelay;
      }
      if (unifiedSettings?.validation_settings?.enabled !== undefined && unifiedSettings.validation_settings.enabled !== prevValidationEnabledRef.current) {
        setValidationEnabled(unifiedSettings.validation_settings.enabled);
        prevValidationEnabledRef.current = unifiedSettings.validation_settings.enabled;
      }
      if (unifiedSettings?.validation_settings?.maxAttempts !== undefined && unifiedSettings.validation_settings.maxAttempts !== prevValidationMaxAttemptsRef.current) {
        setValidationMaxAttempts(unifiedSettings.validation_settings.maxAttempts);
        prevValidationMaxAttemptsRef.current = unifiedSettings.validation_settings.maxAttempts;
      }
      if (unifiedSettings?.validation_settings?.strictValidation !== undefined && unifiedSettings.validation_settings.strictValidation !== prevValidationStrictRef.current) {
        setValidationStrict(unifiedSettings.validation_settings.strictValidation);
        prevValidationStrictRef.current = unifiedSettings.validation_settings.strictValidation;
      }
    }
  }, [unifiedSettings?.messageDelay, unifiedSettings?.showTypingIndicator, unifiedSettings?.buttonOptionsDelay, unifiedSettings?.noResponseDelay, unifiedSettings?.optionsHandlerDelay, unifiedSettings?.validation_settings?.enabled, unifiedSettings?.validation_settings?.maxAttempts, unifiedSettings?.validation_settings?.strictValidation, state.isDirty, state.unsavedChanges]);
  
  const [aiSettings, setAISettings] = useState<AIChatbotSettingsType>(unifiedSettings?.ai_settings ?? {
    messageStyle: 'traditional',
    typingSpeed: 400,
    messageDelay: 500,
    showTypingIndicator: true,
    streamingForAIOnly: true,
    aiServices: {
      primary: {
        provider: 'openai',
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2048
      }
    }
  });
  


  // AI Chat Mode Configuration
  // const aiChatConfig = useAIChatModeConfig(chatbot?.id || '');

  const handleAISettingsChange = (newSettings: AIChatbotSettingsType) => {
    setAISettings(newSettings);
    // Update local settings, don't save yet
    const currentOverallSettings = (unifiedSettings as GeneralChatbotSettings) || { messageDelay: 1000, showTypingIndicator: true };
    const newOverallSettings: GeneralChatbotSettings = {
      ...currentOverallSettings,
      ai_settings: newSettings 
    };
    updateSettings(newOverallSettings as any);
    reportUnsavedChanges(true);
  };
  
  // Simple effect to sync active state from props
  useEffect(() => {
    if (chatbot) {
      setActive(chatbot.active || false);
    }
  }, [chatbot]);

  const handleActiveToggle = (checked: boolean) => {
    setActive(checked);
    // Update local state only, don't save yet
    onUpdateSettings({ active: checked });
    reportUnsavedChanges(true);
  };

  const handleShowTypingIndicatorToggle = (checked: boolean) => {
    console.log('🎚️ Typing indicator changed to:', checked);
    setShowTypingIndicator(checked);
    updateSettings({ showTypingIndicator: checked });
    reportUnsavedChanges(true);
  };

  const handleMessageDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Message delay slider changed to:', delay, 'Type:', typeof delay);
    setMessageDelay(delay);
    updateSettings({ messageDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleButtonOptionsDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Button options delay changed to:', delay);
    setButtonOptionsDelay(delay);
    updateSettings({ buttonOptionsDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleNoResponseDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ No response delay changed to:', delay);
    setNoResponseDelay(delay);
    updateSettings({ noResponseDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleOptionsHandlerDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Options handler delay changed to:', delay);
    setOptionsHandlerDelay(delay);
    updateSettings({ optionsHandlerDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleValidationEnabledChange = (enabled: boolean) => {
    console.log('🎚️ Validation enabled changed to:', enabled);
    setValidationEnabled(enabled);
    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        enabled
      }
    });
    reportUnsavedChanges(true);
  };

  const handleValidationMaxAttemptsChange = (maxAttempts: number) => {
    console.log('🎚️ Validation max attempts changed to:', maxAttempts);
    setValidationMaxAttempts(maxAttempts);
    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        maxAttempts
      }
    });
    reportUnsavedChanges(true);
  };

  const handleValidationStrictChange = (strictValidation: boolean) => {
    console.log('🎚️ Validation strict changed to:', strictValidation);
    setValidationStrict(strictValidation);
    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        strictValidation
      }
    });
    reportUnsavedChanges(true);
  };

  const handleTcpaLanguageChange = (tcpaLanguage: string) => {
    console.log('📞 TCPA language changed to:', tcpaLanguage);

    updateSettings({
      tcpaLanguage
    });
    reportUnsavedChanges(true);
  };

  const handleCustomJavaScriptChange = (value: string) => {
    onUpdateSettings({ 
      custom_javascript: value
    });
    reportUnsavedChanges(true);
  };

  const handleCustomHtmlChange = (value: string) => {
    onUpdateSettings({ 
      custom_html: value
    });
    reportUnsavedChanges(true);
  };

  if (!chatbot) {
    return <div>Loading settings...</div>; 
  }

  return (
    <Tabs defaultValue="general" className="space-y-4">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="aiProvider">AI Provider</TabsTrigger>
        <TabsTrigger value="customCode">Custom Code</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="space-y-6">
        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">General Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="active-toggle">Chatbot Active</Label>
              <Switch 
                id="active-toggle" 
                checked={active} 
                onCheckedChange={handleActiveToggle} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message-delay">Message Delay (ms)</Label>
              <Slider 
                id="message-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[messageDelay]} 
                onValueChange={handleMessageDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {messageDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="button-options-delay">Button Options Delay (ms)</Label>
              <Slider 
                id="button-options-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[buttonOptionsDelay]} 
                onValueChange={handleButtonOptionsDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {buttonOptionsDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="no-response-delay">No Response Delay (ms)</Label>
              <Slider 
                id="no-response-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[noResponseDelay]} 
                onValueChange={handleNoResponseDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {noResponseDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="options-handler-delay">Options Handler Delay (ms)</Label>
              <Slider 
                id="options-handler-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[optionsHandlerDelay]} 
                onValueChange={handleOptionsHandlerDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {optionsHandlerDelay}ms</p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="typing-indicator">Show Typing Indicator</Label>
              <Switch
                id="typing-indicator"
                checked={showTypingIndicator}
                onCheckedChange={handleShowTypingIndicatorToggle}
              />
            </div>

          </div>
        </div>

        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">Validation Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="validation-enabled">Enable Validation</Label>
              <Switch
                id="validation-enabled"
                checked={validationEnabled}
                onCheckedChange={handleValidationEnabledChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validation-max-attempts">Max Attempts</Label>
              <Input
                id="validation-max-attempts"
                type="number"
                value={validationMaxAttempts}
                onChange={(e) => handleValidationMaxAttemptsChange(parseInt(e.target.value))}
                disabled={!validationEnabled}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="validation-strict">Strict Validation</Label>
              <Switch
                id="validation-strict"
                checked={validationStrict}
                onCheckedChange={handleValidationStrictChange}
                disabled={!validationEnabled}
              />
            </div>
          </div>
        </div>

        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">TCPA Compliance</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tcpa-language">TCPA Warning Message</Label>
              <Textarea
                id="tcpa-language"
                placeholder="Enter TCPA consent language (e.g., 'By providing my phone number, I consent to receive text messages from [Company Name]. Message and data rates may apply. Reply STOP to opt out.')"
                value={unifiedSettings?.tcpaLanguage || ''}
                onChange={(e) => handleTcpaLanguageChange(e.target.value)}
                rows={3}
                className="resize-vertical"
              />
              <p className="text-sm text-muted-foreground">
                This message automatically appears when the chatbot asks for a phone number to ensure TCPA compliance.
              </p>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="aiProvider">
        <AIProviderSettings 
          settings={aiSettings} 
          onSettingsChange={handleAISettingsChange} 
        />
        {/* AI Settings are saved via main Save Changes button */}
      </TabsContent>


      <TabsContent value="customCode">
        <div className="space-y-8 p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">Custom Code Injection</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="custom-javascript">Custom JavaScript</Label>
              <Textarea
                id="custom-javascript"
                placeholder="Enter custom JavaScript here..."
                value={unifiedChatbot?.custom_javascript || ''}
                onChange={(e) => {
                  handleCustomJavaScriptChange(e.target.value);
                }}
              />
              <p className="text-sm text-muted-foreground">
                JavaScript code to be injected into the chat widget.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-html">Custom HTML</Label>
              <Textarea
                id="custom-html"
                placeholder="Enter custom HTML here..."
                value={unifiedChatbot?.custom_html || ''}
                onChange={(e) => {
                  handleCustomHtmlChange(e.target.value);
                }}
              />
              <p className="text-sm text-muted-foreground">
                HTML content to be injected into the chat widget.
              </p>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};
